import { makeAutoObservable, runInAction, toJS } from "mobx";
import {
  addESProgramURL,
  allESProgramListURL,
  companyESProgramListURL,
  deleteProgram,
  esProgramListURL,
  esProgramURL,
  getApplicableIndustryURL,
  removeCompanyProgramURL,
} from "../api/url";
import { INITIAL_ES_PROGRAM } from "../constants/externalSupport.constants";
import { toast } from "react-toastify";
import { handleFetch } from "../../../utils/handleFetch";

class ExternalSupportStore {
  isProgramModalOpen = false;
  initialProgram = INITIAL_ES_PROGRAM;
  applicableIndustryList = [];
  programList = [];
  page = 1;
  limit = 10;
  search = "";
  totalPrograms = 0;
  selectedProgramName = "";
  selectedProgramId = "";
  isDeleteConfirmModalOpen = false;
  companyProgramList = [];
  companyESTotal = 0;
  programIds = [];
  applicableIndustries = [];

  constructor() {
    makeAutoObservable(this);
  }

  setPage = (v) => (this.page = v);
  setSearch = (v) => (this.search = v);
  openProgramModal = () => (this.isProgramModalOpen = true);
  closeProgramModal = () => (this.isProgramModalOpen = false);

  _handleESChange = (e) => {
    const { id, value } = e.target;
    this.initialProgram[id] = value;
  };

  handleChangeProgramSupports = (key, value) => {
    this.initialProgram.supports[key] = value;
  };

  createProgram = async () => {
    const body = this.initialProgram;
    const isEdit = Boolean(body._id);
    const url = isEdit ? esProgramURL(body._id) : esProgramURL();
    const method = isEdit ? "PUT" : "POST";

    return await handleFetch({
      url,
      method,
      body,
      onSuccess: (_, message) =>
        runInAction(() => {
          this.errors = {};
          this.isProgramModalOpen = false;
          this.getESPrograms();
          this.initialProgram = INITIAL_ES_PROGRAM;
          toast.success(message);
        }),
      onValidationError: (detail) => runInAction(() => (this.errors = detail)),
    });
  };

  getApplicableIndustries = async () => {
    return await handleFetch({
      url: getApplicableIndustryURL,
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => {
          this.applicableIndustryList = data;
        }),
    });
  };

  getESPrograms = async () => {
    return await handleFetch({
      url:
        esProgramListURL +
        `?page=${this.page}&limit=${this.limit}&search=${this.search}`,
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => {
          const { total, programList } = data;
          this.programList = programList;
          this.totalPrograms = total;
        }),
    });
  };

  getAllESPrograms = async () => {
    return await handleFetch({
      url: allESProgramListURL,
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => {
          const { total, programList } = data;
          this.programList = programList;
          this.totalPrograms = total;
        }),
    });
  };

  getCompanyESProgram = async (isAll = false) => {
    const indList = this.applicableIndustries;
    const url =
      companyESProgramListURL +
      `?page=${this.page}&limit=${this.limit}&search=${this.search}${isAll && "&isAll=true"}`;
    return await handleFetch({
      url,
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => {
          const { total, programList, applicableIndustries } = data;
          this.companyProgramList = programList;
          this.companyESTotal = total;
          this.applicableIndustries =
            this.page === 1 ? applicableIndustries : indList;
        }),
    });
  };

  handleEditProgram = (id) => {
    const programData =
      this.programList?.find((d) => d._id === id) || INITIAL_ES_PROGRAM;
    this.initialProgram = { ...programData };
    this.isProgramModalOpen = true;
  };

  openDeleteConfirmModal = (id, name) => {
    this.selectedProgramName = name;
    this.selectedProgramId = id;
    this.isDeleteConfirmModalOpen = true;
  };

  closeDeleteConfirmModal = () => (this.isDeleteConfirmModalOpen = false);

  handleDeleteProgram = async () => {
    return await handleFetch({
      url: deleteProgram(this.selectedProgramId),
      method: "DELETE",
      onSuccess: (_, message) =>
        runInAction(() => {
          this.isDeleteConfirmModalOpen = false;
          this.getESPrograms();
          toast.success(message);
        }),
    });
  };

  addESProgram = async () => {
    const keys = this.programIds.map((v) => v.key);
    const body = keys.filter((k) => k !== "selectAll");

    if (body.length === 0) return toast.error("Please Choose The Programs");

    return await handleFetch({
      url: addESProgramURL,
      method: "POST",
      body,
      onSuccess: (_, message) =>
        runInAction(() => {
          this.programIds = [];
          this.getCompanyESProgram();
          this.initialProgram = INITIAL_ES_PROGRAM;
          toast.success(message);
        }),
      onValidationError: (detail) => runInAction(() => (this.errors = detail)),
    });
  };

  setESPrograms = (value, programs) => {
    const SELECT_ALL_KEY = "selectAll";

    // const hasSelectAll = (list) => list.some(item => item.key === SELECT_ALL_KEY);
    const selectedIndex = this.programIds.findIndex(
      (p) => p.key === SELECT_ALL_KEY,
    );
    const valueIndex = value.findIndex((v) => v.key === SELECT_ALL_KEY);

    // Case 1: Initial state with only "selectAll"
    if (
      (this.programIds.length === 0 && value[0]?.key === SELECT_ALL_KEY) ||
      (selectedIndex < 0 && valueIndex >= 0)
    ) {
      this.programIds = programs;
      return;
    }

    // Case 2: "selectAll" was deselected
    if (selectedIndex >= 0 && valueIndex < 0) {
      this.programIds = [];
      return;
    }

    // Case 3: "selectAll" toggled while already selected
    if (selectedIndex >= 0 && valueIndex >= 0) {
      this.programIds.splice(selectedIndex, 1);
      value.splice(valueIndex, 1);
    }

    // Default case: Partial selection
    const keys = value.map((v) => v.key);
    this.programIds = keys.map((key) => {
      return this.programIds.find((t) => t.key === key) || { key, value };
    });
  };

  handleChangeESProgram = (e) => {
    const { id, value } = e.target;
    this.initialProgram[id] = value;
    this.programIds = [];
  };

  handleRemoveCompanyProgram = async () => {
    return await handleFetch({
      url: removeCompanyProgramURL(this.selectedProgramId),
      method: "PUT",
      onSuccess: (_, message) =>
        runInAction(() => {
          this.getCompanyESProgram();
          this.isDeleteConfirmModalOpen = false;
          toast.success(message);
        }),
    });
  };
}

export const externalSupportStore = new ExternalSupportStore();
